import React, { useCallback, useMemo } from 'react'
import {
    Box,
    Collapse,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'
import ChatComposer from '../screener/aiChat/ChatComposer'
import AiAssistantEmptyState from './AiAssistantEmptyState'
import ChatHistoryPanel from '../screener/aiChat/ChatHistoryPanel'
import ChatTurn from '../screener/aiChat/ChatTurn'
import { chartCommandResultsFooter } from '../chartV2/aiChat/chartCommandResultsFooter'
import { filterSemanticKey } from '../screener/aiChat/conversationFilters'
import { useChatPanelAutoScroll } from '../screener/aiChat/useChatPanelAutoScroll'
import type { ChatTurnMessage } from '../screener/aiChat/types'
import { useAiAssistant } from './AiAssistantContext'

function StatusDot({ loading, connected, hasError }: { loading: boolean; connected: boolean; hasError: boolean }) {
    const color = hasError ? 'error.main' : loading ? 'warning.main' : connected ? 'success.main' : 'text.disabled'
    return (
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    )
}

function isLastAppliedFilterTurn(message: ChatTurnMessage, messages: ChatTurnMessage[]): boolean {
    let last: ChatTurnMessage | undefined
    for (const m of messages) {
        if (m.role === 'assistant' && m.filterStatus === 'applied' && m.turnId && m.filters?.length) {
            last = m
        }
    }
    return last?.id === message.id
}

function undoStateForMessage(
    message: ChatTurnMessage,
    messages: ChatTurnMessage[],
    activeLedger: ReturnType<typeof useAiAssistant>['ledgerRef']['current'],
    runInFlight: boolean,
): { canUndo: boolean; reason?: string } {
    if (message.filterStatus !== 'applied' || !message.turnId || !message.filters?.length) {
        return { canUndo: false }
    }
    if (runInFlight) return { canUndo: false, reason: 'Wait for the current run to finish' }
    if (!isLastAppliedFilterTurn(message, messages)) return { canUndo: false }
    const deltaKeys = new Set(message.filters.map(filterSemanticKey))
    const ledgerKeys = new Set(activeLedger.map(filterSemanticKey))
    if (![...deltaKeys].some((key) => ledgerKeys.has(key))) return { canUndo: false }
    return { canUndo: true }
}

export default function AiAssistantPanel() {
    const {
        conversationId,
        messages,
        input,
        setInput,
        loading,
        error,
        conversations,
        historyOpen,
        setHistoryOpen,
        wsConnected,
        onSend,
        onRetry,
        onAbort,
        onNewChat,
        switchConversation,
        refreshConversations,
        onUndoTurnFilters,
        undoInFlight,
        userName,
        userAvatarUrl,
        ledgerRef,
    } = useAiAssistant()

    const isCurrentChatEmpty = messages.length === 0 && !loading && !error

    const { scrollableNodeProps, contentRef } = useChatPanelAutoScroll({
        messages,
        loading,
        conversationId,
    })

    const headerTitle = useMemo(() => {
        const title = conversations.find((c) => c.id === conversationId)?.title?.trim()
        return title || 'New chat'
    }, [conversationId, conversations])

    const onToggleHistory = useCallback(() => {
        setHistoryOpen(!historyOpen)
        if (!historyOpen) void refreshConversations()
    }, [historyOpen, refreshConversations, setHistoryOpen])

    const renderedMessages = useMemo(() => {
        const items: React.ReactNode[] = []
        // A user message opens a new exchange, so it gets the wider gap; the
        // assistant reply stays visually tied to the prompt above it.
        let isFirstRendered = true

        for (let i = 0; i < messages.length; i++) {
            const message = messages[i]
            if (message.routingOnly) continue
            const undo =
                message.role === 'assistant'
                    ? undoStateForMessage(message, messages, ledgerRef.current, loading)
                    : { canUndo: false as const }

            const gapTop = isFirstRendered ? 0 : message.role === 'user' ? 3 : 1.75
            isFirstRendered = false

            items.push(
                <Box key={message.id} sx={{ mt: gapTop }}>
                    <ChatTurn
                        message={message}
                        runInFlight={loading}
                        userAvatarUrl={userAvatarUrl}
                        userName={userName}
                        onUndoFilters={message.filters?.length ? onUndoTurnFilters : undefined}
                        undoDisabled={!undo.canUndo || undoInFlight}
                        undoDisabledReason={undo.reason}
                        onRetry={message.showRetry && message.retryPrompt ? onRetry : undefined}
                        retryDisabled={loading}
                        footer={chartCommandResultsFooter(message.content, message.chartCommandResults)}
                    />
                </Box>,
            )
        }
        return items
    }, [
        ledgerRef,
        loading,
        messages,
        onRetry,
        onUndoTurnFilters,
        undoInFlight,
        userAvatarUrl,
        userName,
    ])

    return (
        <Stack sx={{ height: '100%', minHeight: 0 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={1.25}
                borderBottom={1}
                borderColor="divider"
                sx={{ flexShrink: 0 }}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <StatusDot loading={loading} connected={wsConnected} hasError={!!error} />
                    <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                        {headerTitle}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.25}>
                    <Tooltip title={historyOpen ? 'Hide history' : 'Chat history'}>
                        <IconButton
                            size="small"
                            onClick={onToggleHistory}
                            sx={{
                                color: historyOpen ? 'primary.main' : 'text.secondary',
                                bgcolor: historyOpen ? 'action.selected' : 'transparent',
                            }}
                        >
                            <HistoryRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="New chat">
                        <span>
                            <IconButton
                                size="small"
                                onClick={onNewChat}
                                disabled={(!conversationId && !error) || isCurrentChatEmpty}
                            >
                                <AddRoundedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <Collapse in={historyOpen} unmountOnExit>
                <Box sx={{ maxHeight: 280, overflow: 'auto', borderBottom: 1, borderColor: 'divider' }}>
                    <ChatHistoryPanel
                        conversations={conversations}
                        activeConversationId={conversationId}
                        onSelect={switchConversation}
                    />
                </Box>
            </Collapse>

            <Box flex={1} sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SimpleBar style={{ flex: 1, maxHeight: '100%' }} scrollableNodeProps={scrollableNodeProps}>
                    <Box ref={contentRef} sx={{ px: 2, py: 1.5 }}>
                        {isCurrentChatEmpty ? (
                            <AiAssistantEmptyState
                                onSelectPrompt={(text) => onSend(text)}
                                disabled={!conversationId}
                            />
                        ) : (
                            <>
                                {renderedMessages}
                            </>
                        )}
                        {error && (
                            <Typography variant="body2" color="error.main" sx={{ mt: 2 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                </SimpleBar>
            </Box>

            <Box sx={{ px: 1.5, py: 1.25, flexShrink: 0 }}>
                <ChatComposer
                    input={input}
                    onInputChange={setInput}
                    onSend={() => onSend()}
                    onAbort={onAbort}
                    loading={loading}
                    canStop={loading}
                    disabled={!conversationId}
                    placeholder="Describe what you want to see"
                />
            </Box>
        </Stack>
    )
}
